/**
 * HealthPage.tsx — the System Health page (mixed layout, Phase 1).
 *
 * Design: Architecture/20261104_FOWLER_system-health-redesign-design.md;
 * layout: Design/20261104_GARRETT_system-health-layout-mixed.html.
 *
 * Fetches GET /api/health/overview on mount, polls every ~20s, and exposes a
 * "Refresh now" button. One table of containers and jobs grouped Needs
 * attention / Always on / Sleeps when idle / Scheduled jobs / Stopped by
 * choice, then the dependencies once. Rows become cards below `lg`.
 *
 * Failure is a first-class state:
 *   - 401/403  → the clean UnauthorizedScreen.
 *   - BFF unreachable → keep the LAST-KNOWN snapshot + a banner, never blank.
 *   - A source that could not be read shows its reason; the rest still renders.
 *
 * The API client is injected so the page is testable against MSW.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import { UnauthorizedError } from '../../api/client';
import type {
  ComponentGroup,
  ComponentState,
  HealthItem,
  HealthOverview,
  HealthTrends,
  SourceSnapshot,
  SourceStatus,
} from '../../api/types';
import { StatusBadge } from '../../components/StatusBadge';
import { Skeleton } from '../../components/Skeleton';
import { MetricTile } from '../../components/MetricTile';
import { FreshnessIndicator } from '../../components/FreshnessIndicator';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { sortBySeverity } from './severityOrder';
import floating from '../../lib/floating';
import {
  AppHeadline,
  CostCard,
  DependencyTrendLine,
  INITIAL_TRENDS_VIEW,
  TimelineCard,
  TrendsAge,
  StaleNote,
  useAppTrendFields,
  type TrendsView,
} from './TrendsParts';

/** Default UI poll cadence (~20s, design §Polling). Overridable for tests. */
const DEFAULT_POLL_INTERVAL_MS = 20_000;
/** Trends are cached 5 minutes server side; asking once a minute is plenty. */
const DEFAULT_TRENDS_POLL_INTERVAL_MS = 60_000;

export interface HealthPageProps {
  apiClient: ApiClient;
  /** Poll cadence in ms. Defaults to ~20s; injected small in tests. */
  pollIntervalMs?: number;
  /** Trends poll cadence in ms. Defaults to 60s; injected small in tests. */
  trendsPollIntervalMs?: number;
}

type LoadState = 'loading' | 'ready' | 'unauthorized';

type SectionKey = 'needs_attention' | ComponentGroup | 'other';

const SECTION_ORDER: readonly SectionKey[] = [
  'needs_attention',
  'always_on',
  'sleeps_when_idle',
  'scheduled_job',
  'stopped',
  'other',
];

/**
 * Lifts every Failing component into Needs attention whatever its group; an
 * item without `detail` (older backend) lands in Other and is never dropped.
 */
const sectionOf = (item: HealthItem): SectionKey => {
  if (item.detail?.state === 'failing') return 'needs_attention';
  if (!item.detail && item.status === 'down') return 'needs_attention';
  return item.detail?.group ?? 'other';
};

const groupBySection = (items: readonly HealthItem[]): Map<SectionKey, HealthItem[]> => {
  const map = new Map<SectionKey, HealthItem[]>();
  items.forEach((item) => {
    const key = sectionOf(item);
    map.set(key, [...(map.get(key) ?? []), item]);
  });
  return map;
};

// State pills: text label + shape + colour, never colour alone (WCAG 1.4.1).
// Class names are full literals so Tailwind's scanner keeps them.
type PillState = ComponentState | 'unknown';

const PILL_TONE: Record<PillState, string> = {
  running: 'bg-status-ok/15 text-status-ok ring-status-ok/40',
  idle: 'bg-surface-3 text-status-unknown ring-ink-3/30',
  stopped: 'bg-surface-3 text-status-unknown ring-ink-3/30',
  failing: 'bg-status-down/15 text-status-down ring-status-down/50',
  unknown: 'bg-surface-3 text-status-unknown ring-ink-3/30',
};

const PILL_SHAPE: Record<PillState, string> = {
  running: 'rounded-full bg-current', // dot
  idle: 'rounded-full border-2 border-current', // ring
  stopped: 'rounded-[1px] bg-current', // square
  failing: 'bg-current [clip-path:polygon(50%_0,100%_100%,0_100%)]', // triangle
  unknown: 'rotate-45 border-2 border-current', // diamond
};

const StatePill = ({ state, label }: { state: PillState; label: string }) => (
  <span
    data-testid="state-pill"
    data-state={state}
    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-0.5 text-meta font-medium ring-1 ring-inset ${PILL_TONE[state]}`}
  >
    <span aria-hidden="true" data-shape className={`h-2 w-2 shrink-0 ${PILL_SHAPE[state]}`} />
    {label}
  </span>
);

/** "ca-x--0000039" -> "0000039"; anything without the separator is shown whole. */
const revisionSuffix = (name: string | null | undefined): string | null =>
  name ? (name.split('--').pop() ?? name) : null;

const Field = ({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) => (
  <>
    <dt className="text-ink-3">{label}</dt>
    <dd className={`m-0 min-w-0 break-words ${muted ? 'text-ink-3' : 'font-mono text-ink-1'}`}>
      {value}
    </dd>
  </>
);

const MobileLabel = ({ children }: { children: string }) => (
  <span className="mb-1 block text-xs uppercase text-ink-3 lg:hidden">{children}</span>
);

const GRID_COLS = 'lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1.5fr)]';

const ComponentRow = ({ item, view }: { item: HealthItem; view: TrendsView }) => {
  const { t, i18n } = useTranslation();
  const { detail } = item;
  const state: PillState = detail ? (detail.state ?? 'unknown') : 'unknown';
  const isJob = detail?.kind === 'job';
  const dash = t('health.fields.notAvailable');
  let pillLabel = t(`health.state.${state}`);
  if (isJob && detail?.state_label && state !== 'failing') {
    pillLabel = t(`health.jobLabel.${detail.state_label}`, { defaultValue: detail.state_label });
  }
  const failing = sectionOf(item) === 'needs_attention';
  const when = (iso: string | null | undefined): string =>
    iso
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(
          new Date(iso),
        )
      : dash;
  const rev = detail?.revision;
  const trendFields = useAppTrendFields(item, view);
  let replicasText = dash;
  if (detail && detail.replicas !== null)
    replicasText =
      detail.max_replicas === null
        ? String(detail.replicas)
        : t('health.fields.replicasOf', { replicas: detail.replicas, max: detail.max_replicas });

  // The group decides the headline cell: availability, wake-ups or last run.
  let headLabel = t('health.fields.replicas');
  if (isJob) headLabel = t('health.fields.lastRun');
  else if (detail?.group === 'always_on') headLabel = t('health.trends.availability');
  else if (detail?.group === 'sleeps_when_idle') headLabel = t('health.trends.wakeups');
  let headline: string = dash;
  if (isJob) {
    headline = detail?.last_run
      ? `${detail.last_run.status}, ${when(detail.last_run.ended_at)}`
      : dash;
  } else if (detail && detail.replicas !== null) {
    headline =
      detail.max_replicas === null
        ? String(detail.replicas)
        : t('health.fields.replicasOf', { replicas: detail.replicas, max: detail.max_replicas });
  }

  return (
    <li
      data-testid="health-item"
      data-item-key={item.key}
      data-status={item.status}
      data-state={state}
      className={`grid grid-cols-1 gap-3 border-t border-surface-3 px-3 py-3 ${GRID_COLS} ${failing ? 'bg-status-down/10' : ''}`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <b className="min-w-0 break-words text-body text-ink-1">{item.label}</b>
          {detail ? (
            <StatePill state={state} label={pillLabel} />
          ) : (
            <StatusBadge status={item.status} />
          )}
        </div>
        {item.reason ? (
          <p
            data-testid="item-reason"
            className={`mt-1 break-words text-meta ${failing ? 'text-status-down' : 'text-ink-3'}`}
          >
            {item.reason}
          </p>
        ) : null}
      </div>
      <div className="min-w-0">
        <MobileLabel>{headLabel}</MobileLabel>
        {detail && !isJob ? (
          <AppHeadline item={item} view={view} fallback={headline} />
        ) : (
          <span className="break-words text-h2 text-ink-1">{detail ? headline : dash}</span>
        )}
      </div>
      <div className="min-w-0">
        <MobileLabel>{t('health.columns.reliability')}</MobileLabel>
        {isJob || !detail ? (
          <span className="text-meta text-ink-3">{t('health.fields.jobNoReliability')}</span>
        ) : (
          <>
            <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-0.5 text-meta">
              {trendFields.map((f) => (
                <Field key={f.label} label={f.label} value={f.text} muted={f.muted} />
              ))}
              <Field label={t('health.fields.replicas')} value={replicasText} />
              <Field
                label={t('health.fields.restarts')}
                value={detail.restarts === null ? dash : String(detail.restarts)}
              />
              <Field
                label={t('health.fields.newest')}
                value={
                  rev?.newest
                    ? `${revisionSuffix(rev.newest)}${rev.newest_running_state ? ` (${rev.newest_running_state})` : ''}`
                    : dash
                }
              />
              <Field label={t('health.fields.ready')} value={revisionSuffix(rev?.ready) ?? dash} />
            </dl>
            <StaleNote view={view} />
          </>
        )}
      </div>
    </li>
  );
};

const SectionHeader = ({
  title,
  hint,
  failing = false,
}: {
  title: string;
  hint: string | undefined;
  failing?: boolean;
}) => (
  <div
    className={`flex justify-between gap-2 px-3 pb-1 pt-3 text-xs uppercase tracking-wide ${failing ? 'text-status-down' : 'text-ink-3'}`}
  >
    <h3 className="m-0 text-xs font-normal uppercase tracking-wide">{title}</h3>
    {hint ? <span>{hint}</span> : null}
  </div>
);

const ComponentTable = ({ items, view }: { items: readonly HealthItem[]; view: TrendsView }) => {
  const { t } = useTranslation();
  const groups = groupBySection(items);
  const hints: Partial<Record<SectionKey, string>> = {
    needs_attention: t('health.groups.failingCount', {
      count: groups.get('needs_attention')?.length ?? 0,
    }),
    sleeps_when_idle: t('health.groups.wakesOnRequest'),
    stopped: t('health.groups.stoppedHint'),
  };
  return (
    <section
      aria-label={t('health.tableLabel')}
      data-testid="component-table"
      className="overflow-hidden rounded-xl bg-surface-1 ring-1 ring-inset ring-outline"
    >
      <div
        aria-hidden="true"
        className={`hidden gap-3 border-b border-outline px-3 py-1.5 text-xs uppercase tracking-wide text-ink-3 lg:grid ${GRID_COLS}`}
      >
        <span>{t('health.columns.service')}</span>
        <span>{t('health.columns.headline')}</span>
        <span>{t('health.columns.reliability')}</span>
      </div>
      {items.length === 0 ? (
        <p className="m-0 px-3 py-3 text-meta text-ink-3">{t('health.noItems')}</p>
      ) : null}
      {SECTION_ORDER.map((key) => {
        const rows = groups.get(key);
        if (!rows) return null;
        return (
          <div key={key} data-testid="health-section" data-section={key}>
            <SectionHeader
              title={t(`health.groups.${key}`)}
              hint={hints[key]}
              failing={key === 'needs_attention'}
            />
            {key === 'stopped' ? (
              <p
                data-testid="stopped-line"
                className="m-0 flex flex-wrap items-center gap-2 border-t border-surface-3 px-3 py-3 text-ink-3"
              >
                <StatePill state="stopped" label={t('health.state.stopped')} />
                <span className="break-words">{rows.map((r) => r.label).join(', ')}</span>
              </p>
            ) : (
              <ul className="m-0 list-none p-0">
                {rows.map((item) => (
                  <ComponentRow key={item.key} item={item} view={view} />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </section>
  );
};

const DependenciesCard = ({
  source,
  view,
}: {
  source: SourceSnapshot | undefined;
  view: TrendsView;
}) => {
  const { t } = useTranslation();
  const items = sortBySeverity(source?.items ?? [], (item) => item.status);
  return (
    <section
      aria-labelledby="health-deps-heading"
      data-testid="dependencies-card"
      className="flex flex-col gap-3 rounded-xl bg-surface-1 p-4 ring-1 ring-inset ring-outline"
    >
      <h2 id="health-deps-heading" className="text-h2 text-ink-1">
        {t('health.dependenciesTitle')}
      </h2>
      {source?.reason ? (
        <p data-testid="dependencies-reason" className="m-0 break-words text-meta text-ink-3">
          {source.reason}
        </p>
      ) : null}
      {items.length === 0 && !source?.reason ? (
        <p className="m-0 text-meta text-ink-3">{t('health.noItems')}</p>
      ) : null}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {items.map((item) => (
          <li
            key={item.key}
            data-testid="health-item"
            data-item-key={item.key}
            data-status={item.status}
            className="flex flex-col gap-2 rounded-lg bg-surface-2 p-3"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0 break-words text-body font-medium text-ink-1">
                {item.label}
              </span>
              <StatusBadge status={item.status} />
            </div>
            {item.reason ? (
              <p className="m-0 break-words text-meta text-ink-3">{item.reason}</p>
            ) : null}
            <DependencyTrendLine item={item} view={view} />
            {item.metrics.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {item.metrics.map((metric) => (
                  <MetricTile key={`${item.key}-${metric.label}`} metric={metric} />
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <StaleNote view={view} />
    </section>
  );
};

const BANNER_TONE: Record<SourceStatus, string> = {
  down: 'bg-status-down/10 ring-status-down',
  degraded: 'bg-status-degraded/10 ring-status-degraded',
  unknown: 'bg-surface-1 ring-ink-3/30',
  ok: 'bg-surface-1 ring-ink-3/30',
};

const Banner = ({
  overall,
  failingNames,
}: {
  overall: SourceStatus;
  failingNames: readonly string[];
}) => {
  const { t } = useTranslation();
  let key = 'attention';
  if (failingNames.length > 0) key = 'failing';
  else if (overall === 'ok' || overall === 'unknown') key = overall;
  return (
    <div
      role="status"
      data-testid="health-overall"
      data-status={overall}
      className={`rounded-xl p-4 ring-1 ring-inset ${BANNER_TONE[overall]}`}
    >
      <b className="block break-words text-h2 text-ink-1">
        {t(`health.banner.${key}`, { count: failingNames.length, names: failingNames.join(', ') })}
      </b>
      <span className="text-meta text-ink-3">{t(`health.banner.${key}Hint`)}</span>
    </div>
  );
};

const RefreshButton = ({
  onClick,
  refreshing,
  label,
  busyLabel,
}: {
  onClick: () => void;
  refreshing: boolean;
  label: string;
  busyLabel: string;
}) => (
  <button
    type="button"
    data-testid="health-refresh"
    onClick={onClick}
    disabled={refreshing}
    aria-busy={refreshing}
    className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg bg-surface-2 px-4 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-1 disabled:opacity-60"
  >
    {refreshing ? busyLabel : label}
  </button>
);

export const HealthPage = ({
  apiClient,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  trendsPollIntervalMs = DEFAULT_TRENDS_POLL_INTERVAL_MS,
}: HealthPageProps) => {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState<HealthOverview | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [unreachable, setUnreachable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [trends, setTrends] = useState<TrendsView>(INITIAL_TRENDS_VIEW);
  const trendsSeq = useRef(0);
  // Track mount so an in-flight fetch resolving after unmount is a no-op.
  const mountedRef = useRef(true);
  // Monotonic request id: only the LATEST fetch may commit state, so an older
  // slow poll can never clobber a newer response (race-safe without relying on
  // AbortController, which is cross-realm-incompatible under jsdom+undici).
  const requestSeq = useRef(0);
  // Holds the live poll handle so we can stop polling outside the effect (e.g.
  // once the session is unauthorized) without tearing the component down.
  const pollIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIdRef.current !== null) {
      clearInterval(pollIdRef.current);
      pollIdRef.current = null;
    }
  }, []);

  const fetchOverview = useCallback(
    async ({ userInitiated = false }: { userInitiated?: boolean } = {}) => {
      requestSeq.current += 1;
      const seq = requestSeq.current;
      const isCurrent = () => mountedRef.current && seq === requestSeq.current;
      // Only a user-initiated refresh toggles the button's busy state; silent
      // background polls must not disable "Refresh now" every ~20s.
      if (userInitiated) setRefreshing(true);
      try {
        const data = await apiClient.getHealthOverview();
        if (!isCurrent()) return;
        setSnapshot(data);
        setUnreachable(false);
        setState('ready');
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof UnauthorizedError) {
          setState('unauthorized');
          // A 401/403 won't self-heal on the next tick — stop hammering the BFF.
          stopPolling();
          return;
        }
        // Network / 5xx: keep the last-known snapshot, raise the banner.
        // Never blank the board on a transient BFF failure.
        setUnreachable(true);
        setState((prev) => (prev === 'loading' ? 'loading' : 'ready'));
      } finally {
        if (userInitiated && isCurrent()) setRefreshing(false);
      }
    },
    [apiClient, stopPolling],
  );

  // Trends never block or blank the page: a failure keeps the last good value and
  // marks it stale; with no value yet every trends part reads "Unavailable".
  const fetchTrends = useCallback(async () => {
    trendsSeq.current += 1;
    const seq = trendsSeq.current;
    const isCurrent = () => mountedRef.current && seq === trendsSeq.current;
    try {
      const data: HealthTrends = await apiClient.getHealthTrends();
      if (isCurrent()) setTrends({ loaded: true, data, failed: false });
    } catch {
      if (isCurrent()) setTrends((prev) => ({ loaded: true, data: prev.data, failed: true }));
    }
  }, [apiClient]);

  const unauthorized = state === 'unauthorized';
  useEffect(() => {
    if (unauthorized) return undefined;
    floating(fetchTrends());
    const id = setInterval(() => floating(fetchTrends()), trendsPollIntervalMs);
    return () => clearInterval(id);
  }, [fetchTrends, trendsPollIntervalMs, unauthorized]);

  // Mount fetch + ~20s poll.
  useEffect(() => {
    mountedRef.current = true;
    floating(fetchOverview());
    pollIdRef.current = setInterval(() => {
      floating(fetchOverview());
    }, pollIntervalMs);

    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [fetchOverview, pollIntervalMs, stopPolling]);

  const handleRefresh = useCallback(() => {
    floating(fetchOverview({ userInitiated: true }));
    floating(fetchTrends());
  }, [fetchOverview, fetchTrends]);

  // Global "refresh" hotkey (see AppShell) forces an immediate refetch.
  useEffect(() => {
    const onRefresh = () => {
      floating(fetchOverview({ userInitiated: true }));
      floating(fetchTrends());
    };
    window.addEventListener('tcn:refresh', onRefresh);
    return () => window.removeEventListener('tcn:refresh', onRefresh);
  }, [fetchOverview, fetchTrends]);

  if (state === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  // Initial load with no snapshot yet.
  if (state === 'loading' && !snapshot) {
    if (unreachable) {
      return (
        <section aria-labelledby="health-heading" className="flex flex-col gap-4">
          <h1 id="health-heading" className="text-h1 text-ink-1">
            {t('health.title')}
          </h1>
          <p
            role="alert"
            data-testid="health-unreachable-nodata"
            className="text-body text-status-down"
          >
            {t('health.unreachableNoData')}
          </p>
          <div>
            <RefreshButton
              onClick={handleRefresh}
              refreshing={refreshing}
              label={t('health.refresh')}
              busyLabel={t('health.refreshing')}
            />
          </div>
        </section>
      );
    }
    return (
      <section aria-labelledby="health-heading" className="flex flex-col gap-3">
        <h1 id="health-heading" className="text-h1 text-ink-1">
          {t('health.title')}
        </h1>
        <Skeleton data-testid="health-loading" label={t('health.loading')} className="h-40" />
      </section>
    );
  }

  const sourceOf = (key: SourceSnapshot['source']) =>
    snapshot?.sources.find((s) => s.source === key);
  const containers = sourceOf('containers');
  const containerItems = containers?.items ?? [];
  const failingNames = containerItems
    .filter((i) => sectionOf(i) === 'needs_attention')
    .map((i) => i.label);

  return (
    <section aria-labelledby="health-heading" className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 id="health-heading" className="text-h1 text-ink-1">
          {t('health.title')}
        </h1>
        <div className="flex flex-wrap items-center gap-4">
          {snapshot ? (
            <FreshnessIndicator
              timestamp={snapshot.generated_at}
              cacheAgeSeconds={snapshot.cache_age_seconds}
            />
          ) : null}
          <TrendsAge view={trends} />
          <RefreshButton
            onClick={handleRefresh}
            refreshing={refreshing}
            label={t('health.refresh')}
            busyLabel={t('health.refreshing')}
          />
        </div>
      </header>

      {snapshot ? <Banner overall={snapshot.overall} failingNames={failingNames} /> : null}

      {unreachable ? (
        <p
          role="status"
          data-testid="health-unreachable-banner"
          className="rounded-lg bg-status-degraded/15 px-4 py-2 text-meta text-status-degraded ring-1 ring-inset ring-status-degraded/40"
        >
          {t('health.unreachableBanner')}
        </p>
      ) : null}

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-3">
          {containers?.reason ? (
            <p
              data-testid="source-reason"
              className="m-0 break-words rounded-lg bg-status-degraded/10 px-4 py-2 text-meta text-ink-1 ring-1 ring-inset ring-status-degraded/40"
            >
              {t('health.sources.containers')}: {containers.reason}
            </p>
          ) : null}
          <ComponentTable items={containerItems} view={trends} />
          <TimelineCard view={trends} />
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <DependenciesCard source={sourceOf('dependencies')} view={trends} />
          <CostCard view={trends} />
        </div>
      </div>
    </section>
  );
};
